import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, UserPlus, X, AlertCircle, Loader2, RefreshCw, Shield, UserCheck } from 'lucide-react';
import DecryptionService from "../components/decryption"
import { useDrawer } from './manuela';
import UserCreationForm from '@/components/manuela-create-user';

// ============================================
// DECRYPTION SERVICE - Place in separate file
// ============================================
const decryptionService = new DecryptionService();


// ============================================
// MAIN USER LIST COMPONENT
// ============================================
const UserListComponent = () => {
  // State for users fetched from backend
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { openDrawer } = useDrawer();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Refs for dynamic height calculation
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const paginationRef = useRef(null);
  const rowRef = useRef(null);

  const handleCreateUser = () => {
    openDrawer(
      <UserCreationForm />
    );
  };

  // Fields that should be decrypted
  const ENCRYPTED_FIELDS = ['email', 'name']; // Adjust based on what fields are encrypted

  // Calculate items per page based on available height
  const calculateItemsPerPage = useCallback(() => {
    if (!containerRef.current || !headerRef.current || !tableHeaderRef.current || !paginationRef.current) {
      return;
    }

    // Get viewport height
    const viewportHeight = window.innerHeight;
    
    // Calculate fixed heights
    const containerPadding = 64; // 8 * 2 * 4 (p-8 = 32px each side)
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const tableHeaderHeight = tableHeaderRef.current?.offsetHeight || 0;
    const paginationHeight = paginationRef.current?.offsetHeight || 0;
    const gap = 24; // mb-6
    const tableContainerPadding = 0; // No padding on table container
    const rowHeight = 73; // Approximate height of each row (including borders)
    
    // Calculate available height for rows
    const availableHeight = viewportHeight - containerPadding - headerHeight - gap - tableHeaderHeight - paginationHeight - tableContainerPadding - 20; // 20px buffer
    
    // Calculate how many rows can fit
    const possibleItems = Math.floor(availableHeight / rowHeight);
    
    // Set items per page (minimum 3, maximum based on calculation)
    const newItemsPerPage = Math.max(3, possibleItems);
    
    setItemsPerPage(newItemsPerPage);
  }, []);

  // Recalculate when users data changes
  useEffect(() => {
    if (users.length > 0) {
      calculateItemsPerPage();
    }
  }, [users, calculateItemsPerPage]);

  // Set up resize observer and window resize listener
  useEffect(() => {
    // Initial calculation
    calculateItemsPerPage();

    // Recalculate on window resize
    const handleResize = () => {
      calculateItemsPerPage();
    };

    window.addEventListener('resize', handleResize);

    // Also observe container size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateItemsPerPage();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculateItemsPerPage]);

  // Fetch users from backend
  const fetchUsers = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);
      
      // Get the encryption key from logged user data
      // This should come from your auth context/state management
      const userEncryptionKey = null; // TODO: Get from logged user data
      
      // Replace with your backend URL
      const response = await fetch('http://localhost:8000/api/encrypted/users/list');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }
      
      const encryptedData = await response.json();
      
      // Set the encryption key if available
      if (userEncryptionKey) {
        decryptionService.setEncryptionKey(userEncryptionKey);
      }
      
      // Log the raw data received from backend
      console.log('=== RAW USER DATA FROM BACKEND ===');
      console.log('Total users received:', encryptedData.length);
      encryptedData.forEach((user, index) => {
        console.log(`User ${index + 1}:`, user);
        console.log('  - ID:', user.id);
        console.log('  - Email:', user.email);
        console.log('  - Name:', user.name);
        console.log('  - Status:', user.status);
        console.log('  - Created by Admin:', user.created_by_admin);
        console.log('  - Interests:', user.interests);
        console.log('  - All properties:', Object.keys(user));
        console.log('---');
      });
      
      // Try to decrypt if we have a key
      if (decryptionService.isReady()) {
        try {
          const decryptedData = await decryptionService.decryptArray(
            encryptedData, 
            ENCRYPTED_FIELDS
          );
          console.log('=== DECRYPTED USER DATA ===');
          decryptedData.forEach((user, index) => {
            console.log(`Decrypted User ${index + 1}:`, user);
          });
          setUsers(decryptedData);
        } catch (decryptError) {
          console.error('Failed to decrypt user data:', decryptError);
          setUsers(encryptedData); // Fall back to encrypted data
        }
      } else {
        console.log('Decryption service not ready - using encrypted data');
        setUsers(encryptedData);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle reload button click
  const handleReload = () => {
    fetchUsers(false); // Don't show full loading state, just the refresh indicator
  };

  // Extract all unique interests for filter options
  const allInterests = useMemo(() => {
    return [...new Set(users.flatMap(user => user.interests || []))].sort();
  }, [users]);

  const filterOptions = useMemo(() => {
    return ['All', 'Active', 'Inactive', ...allInterests];
  }, [allInterests]);

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (user.interests?.some(interest => interest.toLowerCase().includes(searchTerm.toLowerCase())) || false)
      );
    }

    // Apply status/interest filter
    if (selectedFilter !== 'All') {
      filtered = filtered.filter(user =>
        user.status === selectedFilter || 
        (user.interests?.includes(selectedFilter) || false)
      );
    }

    return filtered;
  }, [users, searchTerm, selectedFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when filters change or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter, itemsPerPage]);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleNewClient = () => {
    handleCreateUser();
  };

  // Function to generate color classes for interest tags
  const getInterestColor = (interest) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-purple-100 text-purple-700',
      'bg-green-100 text-green-700',
      'bg-yellow-100 text-yellow-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-red-100 text-red-700',
      'bg-orange-100 text-orange-700',
    ];
    
    // Generate consistent color based on interest string
    let hash = 0;
    for (let i = 0; i < interest.length; i++) {
      hash = interest.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="h-screen bg-gray-50 p-8 overflow-hidden" ref={containerRef}>
      <div className="h-full max-w-7xl mx-auto flex flex-col">
        {/* Header Section */}
        <div ref={headerRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search users or interests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div className="flex gap-3 items-center">
              {/* Filter Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  <Filter className="w-4 h-4" />
                  <span className="font-medium">{selectedFilter}</span>
                  {selectedFilter !== 'All' && (
                    <X
                      className="w-3 h-3 ml-1 hover:text-red-500 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFilter('All');
                      }}
                    />
                  )}
                </button>
                {showFilterDropdown && (
                  <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[150px] max-h-64 overflow-y-auto">
                    {filterOptions.map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          setSelectedFilter(option);
                          setShowFilterDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reload Button */}
              <button
                onClick={handleReload}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
                disabled={loading || isRefreshing}
                title="Refresh user list"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="font-medium">Reload</span>
              </button>

              {/* New Client Button */}
              <button
                onClick={handleNewClient}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                disabled={loading}
              >
                <UserPlus className="w-4 h-4" />
                <span className="font-medium">New Client</span>
              </button>
            </div>
          </div>
        </div>

        {/* User List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-4" />
              <p className="text-gray-700 font-medium mb-2">Error loading users</p>
              <p className="text-gray-500 text-sm">{error}</p>
              <button
                onClick={() => fetchUsers()}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : currentUsers.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              No users found matching your criteria
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead ref={tableHeaderRef} className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Interests
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentUsers.map((user, index) => (
                      <tr 
                        key={user.id} 
                        ref={index === 0 ? rowRef : null}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.name ? user.name.charAt(0) : '?'}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || <span className="text-gray-400 italic">Encrypted</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {user.email || <span className="text-gray-400 italic">Encrypted</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(user.interests || []).map((interest, index) => (
                              <span
                                key={index}
                                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getInterestColor(interest)}`}
                              >
                                {interest}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.created_by_admin ? (
                            <div className="flex items-center gap-1.5">
                              <Shield className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-600">Admin</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <UserCheck className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-600">Self</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.status === 'Active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div ref={paginationRef} className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white flex-shrink-0">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex gap-1">
                    {totalPages <= 7 ? (
                      // Show all pages if 7 or fewer
                      [...Array(totalPages)].map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPage(index + 1)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === index + 1
                              ? 'bg-blue-600 text-white'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))
                    ) : (
                      // Show abbreviated pagination for many pages
                      <>
                        <button
                          onClick={() => setCurrentPage(1)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === 1
                              ? 'bg-blue-600 text-white'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          1
                        </button>
                        
                        {currentPage > 3 && <span className="px-2 py-1">...</span>}
                        
                        {[...Array(3)].map((_, i) => {
                          const pageNum = currentPage - 1 + i;
                          if (pageNum > 1 && pageNum < totalPages) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-3 py-1 rounded-lg transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'hover:bg-gray-100'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                        
                        {currentPage < totalPages - 2 && <span className="px-2 py-1">...</span>}
                        
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === totalPages
                              ? 'bg-blue-600 text-white'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserListComponent;